import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import './responsive.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { BrowserRouter } from 'react-router-dom'
import { createTheme, ThemeProvider, styled } from '@mui/material/styles'
import { QueryClient, QueryClientProvider } from "react-query"

const theme = createTheme({
	palette: {
		primary: {
			main: '#1a3a52', // Dark blue
			light: '#2d5a7b',
			dark: '#0f2438',
			contrastText: '#ffd700', // Gold
		},
		secondary: {
			main: '#ffd700', // Gold
			light: '#ffed4e',
			dark: '#ccaa00',
			contrastText: '#1a3a52',
		},
		background: {
			default: '#0f1419',
			paper: '#1a2332',
		},
		text: {
			primary: '#ffffff',
			secondary: '#b0b8c1',
		},
		action: {
			active: '#ffd700',
			hover: 'rgba(255, 215, 0, 0.08)',
			selected: 'rgba(255, 215, 0, 0.16)',
			disabled: 'rgba(255, 255, 255, 0.38)',
		},
		divider: 'rgba(255, 215, 0, 0.12)',
		success: {
			main: '#4caf50',
		},
		error: {
			main: '#f44336',
		},
		warning: {
			main: '#ff9800',
		},
		info: {
			main: '#2196f3',
		},
	},
	typography: {
		fontFamily: "'Josefin Sans', 'Poppins', sans-serif",
		body1: {
			color: '#e0e0e0',
			fontSize: '14px',
			lineHeight: 1.6,
		},
		body2: {
			color: '#b0b8c1',
			fontSize: '12px',
			lineHeight: 1.5,
		},
		h1: {
			color: '#ffd700',
			fontWeight: 800,
			fontSize: '32px',
			letterSpacing: '1px',
		},
		h2: {
			color: '#ffd700',
			fontWeight: 800,
			fontSize: '28px',
			letterSpacing: '0.8px',
		},
		h3: {
			color: '#ffd700',
			fontWeight: 800,
			fontSize: '24px',
			letterSpacing: '0.6px',
		},
		h4: {
			color: '#ffd700',
			fontWeight: 700,
			fontSize: '20px',
			letterSpacing: '0.4px',
		},
		h5: {
			color: '#ffd700',
			fontWeight: 700,
			fontSize: '18px',
		},
		h6: {
			color: '#ffd700',
			fontWeight: 700,
			fontSize: '16px',
		},
		subtitle1: {
			color: '#b0b8c1',
			fontSize: '14px',
		},
		subtitle2: {
			color: '#b0b8c1',
			fontSize: '12px',
		},
		caption: {
			color: '#b0b8c1',
			fontSize: '11px',
		},
		button: {
			textTransform: 'uppercase',
			fontWeight: 700,
			letterSpacing: '0.5px',
		},
	},
	components: {
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: '#1a3a52',
					color: '#ffd700',
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: '#1a2332',
					color: '#ffffff',
					borderRight: '1px solid rgba(255, 215, 0, 0.12)',
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 600,
				},
				contained: {
					backgroundColor: '#ffd700',
					color: '#1a3a52',
					'&:hover': {
						backgroundColor: '#ffed4e',
					},
				},
				outlined: {
					borderColor: '#ffd700',
					color: '#ffd700',
					'&:hover': {
						backgroundColor: 'rgba(255, 215, 0, 0.08)',
						borderColor: '#ffed4e',
					},
				},
				text: {
					color: '#ffd700',
					'&:hover': {
						backgroundColor: 'rgba(255, 215, 0, 0.08)',
					},
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'& .MuiOutlinedInput-root': {
						color: '#ffffff',
						backgroundColor: 'rgba(255, 255, 255, 0.05)',
						'& fieldset': {
							borderColor: 'rgba(255, 215, 0, 0.3)',
						},
						'&:hover fieldset': {
							borderColor: '#ffd700',
						},
						'&.Mui-focused fieldset': {
							borderColor: '#ffd700',
						},
					},
					'& .MuiInputBase-input::placeholder': {
						color: 'rgba(255, 255, 255, 0.5)',
						opacity: 1,
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					color: '#ffffff',
					'& input': {
						color: '#ffffff',
					},
				},
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: '#b0b8c1',
					'&.Mui-focused': {
						color: '#ffd700',
					},
				},
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					backgroundColor: '#0f1419',
					boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					'& .MuiTableCell-head': {
						background: 'linear-gradient(135deg, #1a3a52 0%, #2d5a7b 100%)',
						color: '#ffd700',
						fontWeight: 800,
						border: '2px solid #ffd700',
						padding: '14px 10px',
						textTransform: 'uppercase',
						letterSpacing: '0.5px',
						fontSize: '11px',
						boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)',
					},
				},
			},
		},
		MuiTableBody: {
			styleOverrides: {
				root: {
					'& .MuiTableRow-root': {
						backgroundColor: '#162a3d',
						'&:nth-of-type(even)': {
							backgroundColor: '#1a3a52',
						},
						'&:hover': {
							backgroundColor: '#2d5a7b',
							transition: 'background-color 0.3s ease',
						},
					},
					'& .MuiTableCell-body': {
						color: '#ffd700',
						fontWeight: 600,
						border: '1px solid #ffd700',
						padding: '14px 10px',
						textAlign: 'center',
					},
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundColor: '#1a2332',
					color: '#ffffff',
					backgroundImage: 'none',
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				paper: {
					backgroundColor: '#1a2332',
					color: '#ffffff',
				},
			},
		},
		MuiMenu: {
			styleOverrides: {
				paper: {
					backgroundColor: '#1a2332',
					color: '#ffffff',
					'& .MuiMenuItem-root': {
						'&:hover': {
							backgroundColor: 'rgba(255, 215, 0, 0.08)',
						},
					},
				},
			},
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					color: '#ffffff',
					'& .MuiOutlinedInput-notchedOutline': {
						borderColor: 'rgba(255, 215, 0, 0.3)',
					},
					'&:hover .MuiOutlinedInput-notchedOutline': {
						borderColor: '#ffd700',
					},
					'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
						borderColor: '#ffd700',
					},
				},
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					color: 'rgba(255, 215, 0, 0.5)',
					'&.Mui-checked': {
						color: '#ffd700',
					},
				},
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					color: 'rgba(255, 215, 0, 0.5)',
					'&.Mui-checked': {
						color: '#ffd700',
					},
				},
			},
		},
		MuiSwitch: {
			styleOverrides: {
				root: {
					'& .MuiSwitch-switchBase.Mui-checked': {
						color: '#ffd700',
					},
					'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
						backgroundColor: '#ffd700',
					},
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(255, 215, 0, 0.12)',
					color: '#ffd700',
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: 'rgba(255, 215, 0, 0.12)',
				},
			},
		},
		MuiListItem: {
			styleOverrides: {
				root: {
					color: '#ffffff',
					'&:hover': {
						backgroundColor: 'rgba(255, 215, 0, 0.08)',
					},
				},
			},
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					color: '#ffffff',
					'&:hover': {
						backgroundColor: 'rgba(255, 215, 0, 0.08)',
					},
					'&.Mui-selected': {
						backgroundColor: 'rgba(255, 215, 0, 0.16)',
						color: '#ffd700',
					},
				},
			},
		},
		MuiListItemText: {
			styleOverrides: {
				primary: {
					color: '#ffffff !important',
					fontWeight: 600,
				},
				secondary: {
					color: '#b0b8c1 !important',
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					color: '#ffffff',
					'&:hover': {
						backgroundColor: 'rgba(255, 215, 0, 0.08)',
					},
					'&.Mui-selected': {
						backgroundColor: 'rgba(255, 215, 0, 0.16)',
						color: '#ffd700',
					},
				},
			},
		},
		MuiLink: {
			styleOverrides: {
				root: {
					color: '#ffd700',
					'&:hover': {
						color: '#ffed4e',
					},
				},
			},
		},
		MuiCardContent: {
			styleOverrides: {
				root: {
					color: '#ffffff',
				},
			},
		},
		MuiCardHeader: {
			styleOverrides: {
				root: {
					color: '#ffd700',
				},
				title: {
					color: '#ffd700 !important',
					fontWeight: 700,
				},
				subheader: {
					color: '#b0b8c1 !important',
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					color: '#ffffff',
				},
				standardInfo: {
					backgroundColor: 'rgba(33, 150, 243, 0.15)',
					color: '#64b5f6',
				},
				standardSuccess: {
					backgroundColor: 'rgba(76, 175, 80, 0.15)',
					color: '#81c784',
				},
				standardWarning: {
					backgroundColor: 'rgba(255, 152, 0, 0.15)',
					color: '#ffb74d',
				},
				standardError: {
					backgroundColor: 'rgba(244, 67, 54, 0.15)',
					color: '#ef5350',
				},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					backgroundColor: '#1a3a52',
					color: '#ffd700',
					fontSize: '12px',
					border: '1px solid #ffd700',
				},
			},
		},
		MuiBadge: {
			styleOverrides: {
				badge: {
					backgroundColor: '#ffd700',
					color: '#1a3a52',
					fontWeight: 700,
				},
			},
		},
		MuiCircularProgress: {
			styleOverrides: {
				root: {
					color: '#ffd700',
				},
				circle: {
					strokeLinecap: 'round',
				},
			},
		},
		MuiLinearProgress: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(255, 215, 0, 0.12)',
				},
				bar: {
					backgroundColor: '#ffd700',
				},
			},
		},
		MuiSkeleton: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(255, 215, 0, 0.12)',
				},
			},
		},
	},
});
const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<ThemeProvider theme={theme}>
					<App />
				</ThemeProvider>
			</BrowserRouter>
		</QueryClientProvider>
	</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
